import { useCallback, useEffect, useState } from "react";

import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import {
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  gridRowSelectionIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";

import { useOutletApi } from "@/features/authentication";
import { StatusEnum } from "@/features/sales-invoices";
import { formatDate, formatNumber } from "@/features/shared";

// Sales down payments share the sales invoice entity (and therefore the
// sales_invoice_payments join), they are only exposed through their own
// endpoint so permissions can be granted separately. Both sources append to
// the same `salesInvoicePayments` field array, and a single payment may mix
// invoices and down payments.
const PayableTypeEnum = Object.freeze({
  SALES_INVOICE: "SALES_INVOICE",
  SALES_DOWN_PAYMENT: "SALES_DOWN_PAYMENT",
});

const PAYABLE_SOURCES = Object.freeze({
  [PayableTypeEnum.SALES_INVOICE]: {
    label: "Faktur",
    resource: "sales-invoices",
    referenceHeader: "No. Faktur #",
    totalHeader: "Total Faktur",
  },
  [PayableTypeEnum.SALES_DOWN_PAYMENT]: {
    label: "Uang Muka",
    resource: "sales-down-payments",
    referenceHeader: "No. Uang Muka #",
    totalHeader: "Total Uang Muka",
  },
});

export const SelectOutstandingPayableSection = () => {
  const { control } = useFormContext();
  const apiRef = useGridApiRef();
  const [payableType, setPayableType] = useState(
    PayableTypeEnum.SALES_INVOICE
  );
  const [payables, setPayables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPayables, setSelectedPayables] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  const source = PAYABLE_SOURCES[payableType];

  const { append } = useFieldArray({
    control,
    name: "salesInvoicePayments",
  });

  const [buyer, seller, salesInvoicePayments] = useWatch({
    control,
    name: ["buyer", "seller", "salesInvoicePayments"],
    exact: true,
  });

  const outletApi = useOutletApi(seller?.id);

  const fetchOutstandingPayables = useCallback(
    async (searchValue) => {
      if (!buyer || !seller) return [];

      setLoading(true);

      try {
        const unfinishedStatuses = [
          StatusEnum.UNPAID,
          StatusEnum.PARTIALLY_PAID,
        ].join(",");

        const {
          data: { data },
        } = await outletApi.get(source.resource, {
          params: {
            status: unfinishedStatuses,
            buyerableId: buyer?.id,
            referenceNumber: searchValue || null,
            page: paginationModel.page,
            size: paginationModel.pageSize,
          },
        });

        // Exclude already selected invoices / down payments
        const filteredPayables = data.filter(
          (payable) =>
            !salesInvoicePayments.find(
              (payment) => payment.salesInvoiceId === payable.id
            )
        );

        setPayables(filteredPayables);
        setRowCount(filteredPayables.length);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [
      buyer,
      outletApi,
      paginationModel.page,
      paginationModel.pageSize,
      salesInvoicePayments,
      seller,
      source.resource,
    ]
  );

  useEffect(() => {
    fetchOutstandingPayables();
  }, [fetchOutstandingPayables]);

  const handlePayableTypeChange = (_, newPayableType) => {
    if (newPayableType === null) return;

    setPayableType(newPayableType);
    setPayables([]);
    setRowCount(0);
    setSelectedPayables([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = [
    {
      field: "referenceNumber",
      headerName: source.referenceHeader,
      flex: 2,
    },
    {
      field: "transactionDate",
      headerName: "Tanggal",
      flex: 1,
      valueGetter: (value) => formatDate(value),
    },
    {
      field: "note",
      headerName: "Catatan",
      flex: 2,
      valueGetter: (value) => value || "-",
    },
    {
      field: "grandTotal",
      headerName: source.totalHeader,
      type: "number",
      valueFormatter: (value) => formatNumber(value),
      flex: 1,
    },
    {
      field: "outstandingAmount",
      headerName: "Terutang",
      type: "number",
      valueFormatter: (value) => formatNumber(value),
      flex: 1,
    },
  ];

  const submitSelection = () => {
    // Reset the state after submission
    setPayables([]);
    setSelectedPayables([]);

    selectedPayables.forEach(
      ({
        id: salesInvoiceId,
        referenceNumber,
        transactionDate,
        grandTotal,
        outstandingAmount,
      }) => {
        append({
          id: ulid(),
          salesInvoiceId,
          referenceNumber,
          transactionDate,
          grandTotal,
          outstandingAmount,
          allocatedAmount: outstandingAmount,
          discount: 0,
          discountAccounts: [],
        });
      }
    );
  };

  return (
    <Stack spacing={2.5}>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">Ambil Tagihan</Typography>
        <ToggleButtonGroup
          value={payableType}
          exclusive
          onChange={handlePayableTypeChange}
          size="small"
        >
          {Object.entries(PAYABLE_SOURCES).map(([type, { label }]) => (
            <ToggleButton key={type} value={type}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
      <DataGrid
        getRowHeight={() => "auto"}
        apiRef={apiRef}
        columns={columns}
        rows={payables}
        rowCount={rowCount}
        loading={loading}
        checkboxSelection
        onRowSelectionModelChange={() => {
          const selectedModels = Array.from(
            gridRowSelectionIdsSelector(apiRef.current.state).values()
          );
          setSelectedPayables(selectedModels);
        }}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 20, 50]}
      />
      <Button
        fullWidth
        variant="contained"
        disabled={selectedPayables.length === 0}
        sx={{ mt: 1 }}
        onClick={submitSelection}
      >
        Tambah
      </Button>
    </Stack>
  );
};
